import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";
import { rotatorErrorMessage, type AdminGroupOption } from "@/lib/rotators";

export interface RotatorGroup {
  readonly id: string;
  readonly group_jid: string;
  readonly title: string | null;
  readonly position: number;
  readonly enabled: boolean;
  readonly invite_url: string | null;
  readonly invite_status: "pending" | "ready" | "error";
  readonly invite_error: string | null;
  readonly clicks: number;
  /** Membros na ultima atualizacao da lista de grupos (null = desconhecido). */
  readonly size: number | null;
}

export interface Rotator {
  readonly id: string;
  readonly name: string;
  readonly code: string;
  readonly status: "active" | "paused";
  readonly created_at: string;
  readonly group_rotator_groups: readonly RotatorGroup[];
}

export interface GroupRefreshJob {
  readonly id: string;
  readonly status: "pending" | "running" | "done" | "failed";
  readonly error: string | null;
  readonly finished_at: string | null;
}

const ROTATORS_KEY = ["group-rotators"] as const;
const ADMIN_GROUPS_KEY = ["whatsapp-admin-groups"] as const;
const REFRESH_JOB_KEY = ["whatsapp-group-refresh-job"] as const;

async function throwing<T>(promise: PromiseLike<{ data: T; error: { message: string } | null }>): Promise<T> {
  const { data, error } = await promise;
  if (error) throw new Error(rotatorErrorMessage(error.message));
  return data;
}

export function useRotators() {
  return useQuery<Rotator[], Error>({
    queryKey: ROTATORS_KEY,
    queryFn: async () => {
      const rows = await throwing(
        supabase
          .from("group_rotators")
          .select("id, name, code, status, created_at, group_rotator_groups(id, group_jid, title, position, enabled, invite_url, invite_status, invite_error, clicks, size)")
          .order("created_at", { ascending: false }),
      );
      return ((rows ?? []) as Rotator[]).map((r) => ({
        ...r,
        group_rotator_groups: [...r.group_rotator_groups].sort((a, b) => a.position - b.position),
      }));
    },
    // Convite e gerado pelo bot em segundos: recarrega enquanto houver pendente.
    refetchInterval: (query) =>
      query.state.data?.some((r) => r.group_rotator_groups.some((g) => g.invite_status === "pending")) ? 3_000 : false,
  });
}

export function useAdminGroups() {
  return useQuery<AdminGroupOption[], Error>({
    queryKey: ADMIN_GROUPS_KEY,
    queryFn: async () => {
      const rows = await throwing(supabase.from("whatsapp_admin_groups").select("jid, title, size").order("title", { ascending: true }));
      return (rows ?? []) as AdminGroupOption[];
    },
  });
}

/** Ultimo pedido de "Atualizar grupos" (status do bot). */
export function useGroupRefreshJob() {
  const queryClient = useQueryClient();
  return useQuery<GroupRefreshJob | null, Error>({
    queryKey: REFRESH_JOB_KEY,
    queryFn: async () => {
      const row = await throwing(
        supabase
          .from("whatsapp_group_jobs")
          .select("id, status, error, finished_at")
          .eq("kind", "refresh_groups")
          .order("created_at", { ascending: false })
          .limit(1)
          .maybeSingle(),
      );
      const job = (row ?? null) as GroupRefreshJob | null;
      if (job?.status === "done") void queryClient.invalidateQueries({ queryKey: ADMIN_GROUPS_KEY });
      return job;
    },
    refetchInterval: (query) => (query.state.data && ["pending", "running"].includes(query.state.data.status) ? 2_000 : false),
  });
}

function useRpcMutation<V>(rpc: string, toArgs: (v: V) => Record<string, unknown>, invalidate: readonly (readonly string[])[]) {
  const queryClient = useQueryClient();
  return useMutation<unknown, Error, V>({
    mutationFn: (vars) => throwing(supabase.rpc(rpc, toArgs(vars))),
    onSuccess: async () => {
      await Promise.all(invalidate.map((key) => queryClient.invalidateQueries({ queryKey: key })));
    },
  });
}

export function useRequestGroupRefresh() {
  return useRpcMutation<void>("request_whatsapp_group_refresh", () => ({}), [REFRESH_JOB_KEY]);
}

export function useSaveRotator() {
  return useRpcMutation<{ id: string | null; name: string; jids: readonly string[] }>(
    "save_group_rotator",
    (v) => ({ p_id: v.id, p_name: v.name, p_jids: v.jids }),
    [ROTATORS_KEY],
  );
}

export function useSetRotatorPaused() {
  return useRpcMutation<{ id: string; paused: boolean }>("set_group_rotator_paused", (v) => ({ p_id: v.id, p_paused: v.paused }), [ROTATORS_KEY]);
}

export function useDeleteRotator() {
  return useRpcMutation<string>("delete_group_rotator", (id) => ({ p_id: id }), [ROTATORS_KEY]);
}

export function useSetRotatorGroupEnabled() {
  return useRpcMutation<{ groupId: string; enabled: boolean }>(
    "set_group_rotator_group_enabled",
    (v) => ({ p_group_id: v.groupId, p_enabled: v.enabled }),
    [ROTATORS_KEY],
  );
}

export function useRenewRotatorInvite() {
  return useRpcMutation<string>("renew_group_rotator_invite", (groupId) => ({ p_group_id: groupId }), [ROTATORS_KEY]);
}
